import { get } from "@/services/axiosUitils";
import { getFitbitAccessToken } from "../utils/fitbitStorage";


export interface FitnessData {
  steps: number;
  steps_goal: number;
  calories_burned: number;
  calories_goal: number;
  active_zone_minutes: number;
  active_zone_minutes_goal: number;
  resting_heart_rate?: number;
  sleep_minutes: number;
  sleep_goal?: number;
  weight_kg?: number;
  weight_lbs?: number;
  preferred_weight_unit?: string;
  target_weight_kg?: number;
  target_weight_lbs?: number;
  distance_km?: number;
  distance_miles?: number;
  preferred_distance_unit?: string;
  floors?: number;
  // Health Vitals
  spo2_percent?: number;
  breathing_rate?: number;
  skin_temp_celsius?: number;
  skin_temp_fahrenheit?: number;
  preferred_temperature_unit?: string;
  hrv_rmssd?: number;
  bmi?: number;
  body_fat_percent?: number;
  // Stress Data
  stress_score?: number;
  stress_level?: string;
  weekly_steps?: {
    this_week: Array<{
      day: string;
      steps: number;
    }>;
    weekly_average?: number;
  };
  // Nested goals object if API returns it this way
  goals?: {
    steps: number;
    calories: number;
    active_zone_minutes: number;
    sleep: number;
  };
}

export interface StressScoreData {
  success: {
    code: string;
    message: string;
  };
  stress_score: number;
  stress_level: string;
  contributing_factors: {
    hrv: number;
    heart_rate: number;
    sleep_quality: number;
    activity_level: number;
  };
  recommendations?: string[];
  date: string;
}

export interface FitnessApiResponse {
  success:
  | boolean
  | {
    code: string;
    message: string;
  };
  data: FitnessData;
  message?: string;
}

class FitnessService {
  /**
   * Get daily fitness data for a specific date
   * @param date - Date string in YYYY-MM-DD format
   */
  async getDailyFitnessData(date: string): Promise<FitnessApiResponse> {
    const response = await get<FitnessApiResponse>(
      `/fitbit_scan/daily/?date=${date}`
    );

    return response;
  }

  /**
   * Get weekly fitness summary
   */
  async getWeeklyFitnessData(): Promise<FitnessApiResponse> {
    const response = await get<FitnessApiResponse>("/fitbit_scan/weekly/");
    return response;
  }

  /**
   * Get monthly fitness summary
   */
  async getMonthlyFitnessData(): Promise<FitnessApiResponse> {
    const response = await get<FitnessApiResponse>("/fitbit_scan/monthly/");
    return response;
  }

  /**
   * Sync all Fitbit data - Force fetch from Fitbit API and save to DB
   */
  async syncFitbitData(date: string): Promise<FitnessApiResponse> {
    try {
      // Get Fitbit token for sync authorization
      const fitbitToken = getFitbitAccessToken();

      // Build headers with Fitbit token if available
      const headers: Record<string, string> = {};
      if (fitbitToken) {
        headers["X-Fitbit-Authorization"] = `Bearer ${fitbitToken}`;
      }

      // Call backend SYNC-ALL endpoint with date parameter
      const response = await get<any>(`/fitbit_scan/sync-all/?date=${date}`, {
        headers,
      });

      // Check response structure: { success: {...}, message: "Successfully synced" }
      if (response?.success || response?.message === "Successfully synced") {
        // After successful sync, fetch fresh data from DB for the requested date
        return await this.getDailyFitnessData(date);
      }

      throw new Error(response?.message ?? "Failed to sync Fitbit data");
    } catch (error: any) {
      throw new Error(error?.message ?? "Sync failed");
    }
  }

  /**
   * Get stress score data for a specific date
   */
  async getStressScore(date: string): Promise<StressScoreData> {
    try {
      const response = await get<StressScoreData>(
        `/fitbit_scan/stress-score/?date=${date}`
      );
      return response;
    } catch (error: any) {
      throw new Error(error?.message ?? "Failed to fetch stress score");
    }
  }

  /**
   * Check if Fitbit is connected (has valid token OR refresh token)
   */
  hasFitbitConnected(): boolean {
    // Check if we have a valid access token
    const accessToken = getFitbitAccessToken();
    if (accessToken) {
      return true;
    }

    // If access token expired, check if we have refresh token
    const refreshToken = localStorage.getItem("glucera_fitbit_refresh_token");
    return !!refreshToken;
  }
}

const fitnessService = new FitnessService();
export default fitnessService;
